import {
  BarChart3,
  Beef,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Coffee,
  FileSignature,
  FileText,
  LayoutDashboard,
  ListChecks,
  Package,
  Pill,
  Settings,
  ShoppingBag,
  Star,
  Store,
  Tags,
  UtensilsCrossed,
  Wallet,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppShell, type ShellNavItem } from '@/components/app-shell';
import { use_session_store } from '@/lib/session-store';
import { use_draft } from '@/features/onboarding/hooks';
import { VendorStatusBanner } from '@/components/vendor-status-banner';

const DINING_MODULES = ['CAFE', 'RESTAURANT'];

/**
 * Tabs are module-aware: a café/restaurant sees dining setup, reservations &
 * passes, and menu — never the vet-style "Services" or generic slot list — while
 * a vet/walker/etc. sees Services + Availability and no menu/orders. A walker
 * additionally sees a read-only Walk plans view for recurring subscriptions. A
 * pharmacy or packaged-food vendor is commerce-only like dining (Products +
 * Orders) but — unlike dining — has no bookable slots at all, so a
 * pharmacy/food-only vendor gets no Services, Availability, or Bookings tab
 * either. Both share one "Orders" tab (the commerce shipments inbox), since a
 * shipment is just one vendor's paid slice of a cross-vendor commerce order.
 */
function nav_for_modules(modules: string[]): ShellNavItem[] {
  const has_dining = modules.some((m) => DINING_MODULES.includes(m));
  const has_pharmacy = modules.includes('PHARMACY');
  const has_food = modules.includes('FOOD');
  const commerce_only = (has_pharmacy || has_food) && modules.every((m) => m === 'PHARMACY' || m === 'FOOD');
  const has_service = modules.some((m) => !DINING_MODULES.includes(m) && m !== 'PHARMACY' && m !== 'FOOD');
  const has_walker = modules.includes('WALKER');
  const walker_only = has_walker && modules.every((m) => m === 'WALKER');
  const has_trainer = modules.includes('TRAINER');
  const trainer_only = has_trainer && modules.every((m) => m === 'TRAINER');
  const has_groomer = modules.includes('GROOMER');
  const groomer_only = has_groomer && modules.every((m) => m === 'GROOMER');
  const has_cremation = modules.includes('CREMATION');
  const cremation_only = has_cremation && modules.every((m) => m === 'CREMATION');
  const has_lab = modules.includes('LAB');
  const lab_only = has_lab && modules.every((m) => m === 'LAB');
  const dining_only = has_dining && !has_service;

  const nav: ShellNavItem[] = [
    { to: '/vendor/app', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'nav-dashboard' },
  ];
  if (has_service)
    nav.push({
      to: '/vendor/app/services',
      label: walker_only
        ? 'Walk services'
        : trainer_only
          ? 'Training services'
          : groomer_only
            ? 'Grooming services'
            : cremation_only
              ? 'Cremation services'
              : lab_only
                ? 'Lab tests'
                : 'Services',
      icon: ListChecks,
      testid: 'nav-services',
    });
  if (has_dining) {
    nav.push({ to: '/vendor/app/dining', label: 'Dining setup', icon: Coffee, testid: 'nav-dining' });
    nav.push({ to: '/vendor/app/offerings', label: 'Reservations & passes', icon: Tags, testid: 'nav-offerings' });
    nav.push({ to: '/vendor/app/menu', label: 'Menu', icon: UtensilsCrossed, testid: 'nav-menu' });
  }
  if (has_pharmacy) {
    nav.push({ to: '/vendor/app/pharmacy-products', label: 'Products', icon: Pill, testid: 'nav-pharmacy-products' });
  }
  if (has_food) {
    nav.push({ to: '/vendor/app/food-products', label: 'Products', icon: Beef, testid: 'nav-food-products' });
  }
  // Cremation is on-demand and pharmacy/food are commerce-only — none of them have bookable slots.
  if (!cremation_only && !commerce_only) {
    nav.push({
      to: '/vendor/app/availability',
      label: dining_only ? 'Reservation hours' : 'Availability',
      icon: CalendarClock,
      testid: 'nav-availability',
    });
    /*
     * Sits next to Availability because it is the read side of the same thing: Availability sets
     * the weekly rules, Schedule shows what those rules plus real bookings produced on each
     * date - including dates in the past, which Availability has no notion of.
     */
    nav.push({
      to: '/vendor/app/schedule',
      label: 'Schedule',
      icon: CalendarDays,
      testid: 'nav-schedule',
    });
  }
  if (!commerce_only) {
    nav.push({ to: '/vendor/app/bookings', label: cremation_only || lab_only ? 'Requests' : 'Bookings', icon: CalendarCheck, testid: 'nav-bookings' });
  }
  if (has_walker) nav.push({ to: '/vendor/app/walk-plans', label: 'Walk plans', icon: CalendarRange, testid: 'nav-walk-plans' });
  // Customer-bought recurring training courses (accept/reject inbox) — the trainer analogue of Walk plans.
  if (has_trainer) nav.push({ to: '/vendor/app/training-packages', label: 'Training plans', icon: CalendarRange, testid: 'nav-training-packages' });
  // Vendor-defined 7/15/30-day bundles of walks or classes (client change, 9 Oct 2026).
  if (has_walker || has_trainer) nav.push({ to: '/vendor/app/packages', label: 'Packages', icon: Package, testid: 'nav-packages' });
  if (has_groomer) nav.push({ to: '/vendor/app/grooming-packages', label: 'Grooming packages', icon: Package, testid: 'nav-grooming-packages' });
  if (has_groomer) nav.push({ to: '/vendor/app/groom-reports', label: 'Groom reports', icon: FileText, testid: 'nav-groom-reports' });
  if (has_dining) nav.push({ to: '/vendor/app/orders', label: 'Orders', icon: ShoppingBag, testid: 'nav-orders' });
  // Pharmacy and food orders are both paid commerce shipments — one shared inbox.
  if (has_pharmacy || has_food) {
    nav.push({ to: '/vendor/app/commerce-shipments', label: 'Orders', icon: ShoppingBag, testid: 'nav-commerce-shipments' });
  }
  nav.push({ to: '/vendor/app/reviews', label: 'Reviews', icon: Star, testid: 'nav-reviews' });
  nav.push({ to: '/vendor/app/reports', label: 'Reports', icon: BarChart3, testid: 'nav-reports' });
  nav.push({ to: '/vendor/app/payments', label: 'Payments', icon: Wallet, testid: 'nav-payments' });
  nav.push({ to: '/vendor/app/agreements', label: 'Agreements', icon: FileSignature, testid: 'nav-agreements' });
  nav.push({ to: '/vendor/app/settings', label: 'Settings', icon: Settings, testid: 'nav-settings' });
  return nav;
}

export function VendorShell() {
  const navigate = useNavigate();
  const phone = use_session_store((s) => s.phone);
  const clear = use_session_store((s) => s.clear);
  const draft_q = use_draft();
  const nav = nav_for_modules(draft_q.data?.module_types ?? []);

  function handle_sign_out() {
    navigate('/', { replace: true });
    clear();
  }

  return (
    <AppShell
      brandIcon={Store}
      brandTitle="Vendor Workspace"
      nav={nav}
      phone={phone}
      bookingPath="/vendor/app/bookings"
      profilePath="/vendor/app/profile"
      onSignOut={handle_sign_out}
      navTestId="vendor-nav"
      mainTestId="vendor-main"
      maxWidthClass="max-w-6xl"
      banner={<VendorStatusBanner />}
    />
  );
}
